import { useId, useState, type ReactNode } from "react";
import { DenseTable, StatusBadge, useDialogFocus, type Column } from "../../components/index.ts";
import type { RoleState } from "../../state/identity.tsx";
import { WriteButton, ReadOnlyNote, canWrite } from "./WriteControls.tsx";
import { useAlertChannels, useCreateAlertChannel } from "./use-alerts.ts";
import {
  deliveryLevel,
  deliveryStatusLabel,
  formatTime,
  type AlertChannel,
  type AlertChannelInput,
} from "./vocab.ts";

/**
 * The notification Channels region (task 11.13; design 11 Alerts row, FR-10.4, 12.4). It lists
 * channels (`GET /alerts/channels`, viewer) with their delivery status, and creates signed-webhook
 * channels (`POST /alerts/channels`, operator) through a labelled form.
 *
 * SECRET HANDLING (the contract's `AlertChannelInput.signing_secret` is `writeOnly`, min 32 chars,
 * and is NEVER returned): the create form collects the HMAC secret in a password-type input so it is
 * not shoulder-surfed; the secret is sent once in the create body and then DROPPED from component
 * state on success. It is never read back, never rendered, and the server does not echo it (the
 * `AlertChannel` the list shows has no secret field — only a `target` with credentials and query
 * stripped). The one-time secret is the operator's to keep; the UI tells them so and shows it
 * nowhere afterwards.
 */
export function Channels(props: { role: RoleState }): ReactNode {
  const query = useAlertChannels();
  const [creating, setCreating] = useState(false);

  const columns: readonly Column<AlertChannel>[] = [
    {
      key: "enabled",
      header: "Enabled",
      width: "sm",
      cell: (c) =>
        c.enabled ? (
          <StatusBadge level="neutral" label="Enabled" size="sm" />
        ) : (
          <StatusBadge level="unknown" label="Disabled" size="sm" />
        ),
    },
    { key: "kind", header: "Kind", width: "sm", cell: (c) => c.kind },
    {
      key: "target",
      header: "Target",
      isRowHeader: true,
      cell: (c) => <span className="alr-mono">{c.target}</span>,
    },
    {
      key: "delivery",
      header: "Delivery",
      cell: (c) => (
        <StatusBadge
          level={deliveryLevel(c.delivery.status)}
          label={deliveryStatusLabel(c.delivery.status)}
          size="sm"
          title={deliveryTitle(c)}
        />
      ),
    },
    {
      key: "last",
      header: "Last success",
      cell: (c) => formatTime(c.delivery.last_success_at),
    },
  ];

  return (
    <section className="alr-region" aria-label="Notification channels">
      <div className="alr-region__head">
        <h2 className="alr-region__title">Channels</h2>
        <WriteButton role={props.role} onClick={() => setCreating(true)} variant="primary">
          New channel
        </WriteButton>
      </div>
      <ReadOnlyNote role={props.role} />

      {query.isError && !query.error.isWaking ? (
        <p className="alr-field__error" role="alert">
          Channels could not load: {query.error.message}
        </p>
      ) : (
        <DenseTable
          caption="Notification channels with delivery status. The target is shown with credentials stripped."
          columns={columns}
          rows={query.data?.items ?? []}
          rowKey={(c) => c.id}
          emptyMessage="No channels. Create a signed webhook to receive alert notifications."
        />
      )}

      {creating && <ChannelForm role={props.role} onClose={() => setCreating(false)} />}
    </section>
  );
}

/* ---- Create form (write-only secret) -------------------------------------------------------- */

function ChannelForm(props: { role: RoleState; onClose: () => void }): ReactNode {
  const titleId = useId();
  const [target, setTarget] = useState("");
  const [secret, setSecret] = useState("");
  const [enabled, setEnabled] = useState(true);
  const mutation = useCreateAlertChannel();

  const targetError = validateTarget(target);
  const secretError = validateSecret(secret);
  const canSubmit = canWrite(props.role) && targetError === null && secretError === null && !mutation.isPending;

  const submit = (): void => {
    if (!canSubmit) return;
    const body: AlertChannelInput = {
      kind: "webhook",
      target: target.trim(),
      signing_secret: secret,
      enabled,
    };
    mutation.mutate(body, {
      onSuccess: () => {
        // Drop the secret from state the moment the write succeeds; it is never read back or shown.
        setSecret("");
        props.onClose();
      },
    });
  };

  // Trap/restore focus and wire Escape-to-close for the modal (design 12.4).
  const dialogRef = useDialogFocus<HTMLDivElement>(props.onClose);

  return (
    <div className="alr-dialog-backdrop" role="presentation" onClick={props.onClose}>
      <div
        ref={dialogRef}
        className="alr-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id={titleId} className="alr-dialog__title">
          New signed-webhook channel
        </h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <label className="alr-field">
            <span className="alr-field__label">Target URL (https, required)</span>
            <input
              className="alr-input"
              type="url"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder="https://example.com/hooks/oathera"
              maxLength={2048}
              required
              aria-invalid={target.length > 0 && targetError !== null}
            />
            {target.length > 0 && targetError !== null && (
              <span className="alr-field__error" role="alert">
                {targetError}
              </span>
            )}
          </label>

          <label className="alr-field">
            <span className="alr-field__label">Signing secret (required, at least 32 characters)</span>
            <input
              className="alr-input"
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              minLength={32}
              maxLength={256}
              required
              autoComplete="off"
              aria-invalid={secret.length > 0 && secretError !== null}
            />
            {secret.length > 0 && secretError !== null && (
              <span className="alr-field__error" role="alert">
                {secretError}
              </span>
            )}
            <span className="alr-field__hint alr-faint">
              Used to sign <code>X-Oathera-Signature</code>. It is write-only. Store it now; the control
              plane never shows it again.
            </span>
          </label>

          <label className="alr-field alr-field--checkbox">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            <span className="alr-field__label">Enabled</span>
          </label>

          {mutation.isError && (
            <p className="alr-field__error" role="alert">
              {mutation.error.kind === "forbidden"
                ? "The server did not permit this. Creating a channel requires the operator role."
                : mutation.error.message}
            </p>
          )}

          <div className="alr-dialog__actions">
            <WriteButton role={props.role} type="submit" variant="primary" disabled={!canSubmit} onClick={submit}>
              {mutation.isPending ? "Creating…" : "Create channel"}
            </WriteButton>
            <button type="button" className="alr-btn" data-variant="ghost" onClick={props.onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---- validation ----------------------------------------------------------------------------- */

/** The target must be a non-empty https URL (contract `pattern: ^https://`). */
function validateTarget(target: string): string | null {
  const t = target.trim();
  if (t.length === 0) return "A target URL is required.";
  if (!/^https:\/\//i.test(t)) return "The target must be an https URL.";
  if (t.length > 2048) return "The target URL is too long.";
  return null;
}

/** The signing secret must be 32–256 characters (contract bound). */
function validateSecret(secret: string): string | null {
  if (secret.length === 0) return "A signing secret is required.";
  if (secret.length < 32) return "The signing secret must be at least 32 characters.";
  if (secret.length > 256) return "The signing secret must be at most 256 characters.";
  return null;
}

/** A fuller delivery description for the badge's accessible name. */
function deliveryTitle(c: AlertChannel): string {
  const d = c.delivery;
  const bits = [deliveryStatusLabel(d.status)];
  if (d.consecutive_failures > 0) {
    bits.push(`${d.consecutive_failures} consecutive failures`);
  }
  if (d.last_attempt_at !== null) bits.push(`last attempt ${formatTime(d.last_attempt_at)}`);
  return bits.join(" · ");
}
